/**
 * Shared step-ordinal registry for Atlas onboarding step machines.
 *
 * The server enforces **max-ordinal semantics** on every `PATCH /api/onboarding-state`:
 * if two users advance the same machine concurrently, only the larger ordinal
 * wins. That prevents a race where user A finishes step 3 just as user B
 * clicks "Back" to step 1 — without the monotonic guard, the server would
 * accept the "1" write and silently un-do the 3.
 *
 * Terminal steps:
 *   * `completed` always gets ordinal 99 (highest — wins over anything).
 *   * `skipped`   also gets ordinal 99 but is a separate terminal marker.
 *
 * Keep this file in sync with `server/lib/onboardingStepOrdinals.js`. A unit
 * test asserts the two registries are byte-for-byte equal.
 */

export const TERMINAL_ORDINAL = 99;

/** Sentinel steps applied at the end of every machine. */
export const TERMINAL_STEPS = ['completed', 'skipped'] as const;
export type TerminalStep = (typeof TERMINAL_STEPS)[number];

/**
 * Machine key  -> ordered list of non-terminal step names.
 * Ordinal = index + 1. Terminal steps (`completed`, `skipped`) always map to
 * `TERMINAL_ORDINAL`.
 */
/*
 * IMPORTANT: the step order here must match the runtime order in
 * `src/shared/onboarding/onboardingState.ts` (FORECAST_TOUR_ORDER,
 * RELEASES_TOUR_ORDER, POST_UPDATE_JOURNEY_STEPS) AND the mission.steps
 * arrays in `src/features/atlas/missions/*.ts`. Any drift causes the
 * server's max-ordinal guard to treat perfectly legal forward moves as
 * backwards races and silently drop them.
 */
export const STEP_MACHINES = {
  'mission.welcome': ['started'],

  // Forecast page guided tour (see FORECAST_TOUR_ORDER in onboardingState.ts).
  'mission.forecast': ['welcome', 'summary', 'filters', 'tasks', 'distribute'],

  // Releases page guided tour (see RELEASES_TOUR_ORDER in onboardingState.ts).
  'mission.releases': ['welcome', 'calendar', 'mapping', 'plans', 'nextForecast'],

  // Post-update journey (see POST_UPDATE_JOURNEY_STEPS in onboardingState.ts).
  'mission.postUpdateJourney': [
    'dateFilter',
    'tabDetails',
    'tabEstimation',
    'navigateCalendar',
    'navigateReleases',
  ],

  // Atlas storage:'atlas' missions. Step ids come from each mission's
  // `steps: [{ id: '...' }]` in `src/features/atlas/missions/*.ts`.
  'mission.wire-integrations': ['org-jira', 'product-jira', 'sharepoint', 'test-save'],
  'mission.load-analytics-data': [
    'connect',
    'full-update',
    'date-filter',
    'details-tab',
    'estimation-tab',
    'calendar-setup',
  ],
  'mission.calendar-connect': [
    'setup',
    'sync',
    'filters',
    'month-view',
    'author-mappings',
    'keywords',
    'public-holiday-keywords',
    'next-releases',
  ],
  'mission.first-baseline': ['intro', 'create', 'compare'],
  // Iter 7: first-report rebuilt with 5 steps and the new Reports surface
  // anchors. Step ids match `src/features/atlas/missions/reports.ts`.
  'mission.first-report': [
    'choose-template',
    'configure-scope',
    'arrange-blocks',
    'preview',
    'export',
  ],
} as const satisfies Record<string, readonly string[]>;

export type MachineKey = keyof typeof STEP_MACHINES;

/**
 * Look up the ordinal for one step of one machine. Unknown machines /
 * non-terminal steps return `null` so the caller can distinguish "missing
 * from registry" from "step 0".
 */
export function ordinalFor(machineKey: string, step: string): number | null {
  if (step === 'completed' || step === 'skipped') return TERMINAL_ORDINAL;
  const machine = (STEP_MACHINES as Record<string, readonly string[] | undefined>)[machineKey];
  if (!machine) return null;
  const idx = machine.indexOf(step);
  if (idx === -1) return null;
  return idx + 1;
}

/**
 * Reverse lookup: given an ordinal, return the canonical step name. Used by
 * the client when normalising server payloads where only `ordinal` was
 * persisted. Returns `null` when out of range.
 */
export function stepForOrdinal(machineKey: string, ordinal: number): string | null {
  if (ordinal === TERMINAL_ORDINAL) return 'completed';
  const machine = (STEP_MACHINES as Record<string, readonly string[] | undefined>)[machineKey];
  if (!machine) return null;
  if (ordinal < 1 || ordinal > machine.length) return null;
  return machine[ordinal - 1] ?? null;
}

/** True when `step` is the final non-terminal step for the machine (next advance -> completed). */
export function isLastNonTerminal(machineKey: string, step: string): boolean {
  const machine = (STEP_MACHINES as Record<string, readonly string[] | undefined>)[machineKey];
  if (!machine) return false;
  return machine[machine.length - 1] === step;
}
